import { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { mountScrollWorld } from '../lib/scroll-world/scrub-engine';
import { installVariantToggle, restoreFlightPosition } from '../lib/scroll-world/variant-toggle';
import { installFlightTracking } from '../lib/flight-analytics';
import { BETA_COPY, primaryCta } from '../lib/signup';

// A/B experiment Variant A — "The Quiet Floors": scroll-scrubbed dawn ascent
// through a cutaway studio building where every floor is a LYKBL product
// surface. Not prerendered, noindexed.
// Assets are served from a CDN (Supabase Storage public bucket — CORS `*` + HTTP
// range requests + a 1-year immutable cache), keeping ~140 MB of video out of the
// repo (#211). Override with VITE_FLIGHT_CDN_BASE to point at a different CDN.
const FLIGHT_BASE =
  import.meta.env.VITE_FLIGHT_CDN_BASE ||
  'https://qdvlzcqejhyceqrluzvl.supabase.co/storage/v1/object/public/marketing-flight';
const ASSETS = `${FLIGHT_BASE}/quiet-floors`;

const TOPBAR_CTA = primaryCta('flight_a_topbar');
const FINAL_CTA = primaryCta('flight_a_final');

// Exported so FlightLanding can prerender the scene copy for crawlers.
// eslint-disable-next-line react-refresh/only-export-components
export const CONFIG = {
  brand: { name: 'lykbl', href: '/' },
  cta: { label: TOPBAR_CTA.shortLabel, href: TOPBAR_CTA.href },
  hint: 'scroll to step inside',
  diveScroll: 1.35,
  sections: [
    {
      id: 'facade',
      label: 'The Facade',
      still: `${ASSETS}/still1.jpg`,
      clip: `${ASSETS}/scene1.mp4`,
      clipMobile: `${ASSETS}/m/scene1.mp4`,
      accent: '#0e7c6e',
      scroll: 1.5,
      linger: 0.35,
      eyebrow: 'Your AI agent team · you approve every move',
      title: 'Grow your audience. Turn attention into offers.',
      body: 'A team of AI specialists that researches your audience, drafts your content, and shapes your offers — brand pitches, bookings, and paid communities. Every floor waits for your approval.',
      tags: ['Review-first by default', 'Official APIs only — no scraping', 'Tuned to your voice'],
    },
    {
      id: 'signals',
      label: 'Signals',
      still: `${ASSETS}/still2.jpg`,
      clip: `${ASSETS}/scene2.mp4`,
      clipMobile: `${ASSETS}/m/scene2.mp4`,
      accent: '#0e7c6e',
      eyebrow: 'Command centre',
      title: 'Your platforms, one command centre.',
      body: 'Stop juggling dashboards. Connect supported channels to see their signals side by side — with one queue for everything your agents prepare. More platforms are on the way.',
      tags: ['YouTube', 'LinkedIn', 'TikTok', 'X', 'More coming'],
    },
    {
      id: 'handoff',
      label: 'The Handoff',
      still: `${ASSETS}/still3.jpg`,
      clip: `${ASSETS}/scene3.mp4`,
      clipMobile: `${ASSETS}/m/scene3.mp4`,
      accent: '#c46c2a',
      eyebrow: 'How it works',
      title: 'Connect once. Brief once.',
      body: 'Connect the channels you want signals from, then give one brief — your agents pass the work between floors from there, guided by your brief and voice notes.',
      tags: ['01 Brief the team once', '02 Agents draft your moves'],
    },
    {
      id: 'team',
      label: 'The Team',
      still: `${ASSETS}/still4.jpg`,
      clip: `${ASSETS}/scene4.mp4`,
      clipMobile: `${ASSETS}/m/scene4.mp4`,
      accent: '#c46c2a',
      eyebrow: 'The team',
      title: 'Agents do the work. You make the calls.',
      body: 'Specialist agents research, draft, and plan tests — handing annotated pages to each other like a real team. Nameplates on, chairs empty: the work keeps moving so you don’t have to.',
      tags: ['Mira reads signals', 'Caleb drafts hooks & scripts', 'Lena plans tests'],
    },
    {
      id: 'review',
      label: 'The Review Gate',
      still: `${ASSETS}/still5.jpg`,
      clip: `${ASSETS}/scene5.mp4`,
      clipMobile: `${ASSETS}/m/scene5.mp4`,
      accent: '#0e7c6e',
      eyebrow: 'You set the autonomy',
      title: 'Every draft queues for your yes.',
      body: 'Review every output yourself — nothing is published or sent without your approval. Earned autonomy for proven workflows is on the roadmap.',
      tags: ['Review-first by default', 'Autonomy is earned'],
    },
    {
      id: 'skylight',
      label: 'The Revenue Desk',
      still: `${ASSETS}/still6.jpg`,
      clip: `${ASSETS}/scene6.mp4`,
      clipMobile: `${ASSETS}/m/scene6.mp4`,
      accent: '#c46c2a',
      scroll: 1.7,
      linger: 0.45,
      eyebrow: 'Earn',
      title: 'Keep every opportunity in one place.',
      body: `The brand deals and bookings you track sit in tidy trays with follow-ups drafted in your voice, ready for your review — and the whole building rises to meet the morning. ${BETA_COPY.pitch}`,
      tags: [],
      cta: {
        primary: { label: FINAL_CTA.label, href: FINAL_CTA.href },
        secondary: { label: 'Find out more', href: '/home' },
      },
    },
  ],
  // Frame-locked aerial connectors (one per seam, length = sections - 1): each was
  // generated from the previous scene's last frame → next scene's first frame, so
  // the seams are pixel-identical. Set any entry to null to fall back to a crossfade.
  connectors: [
    `${ASSETS}/conn1.mp4`,
    `${ASSETS}/conn2.mp4`,
    `${ASSETS}/conn3.mp4`,
    `${ASSETS}/conn4.mp4`,
    `${ASSETS}/conn5.mp4`,
  ],
  // Lighter 720p tight-GOP encodes served on phones (engine picks these on
  // coarse-pointer / narrow viewports).
  connectorsMobile: [
    `${ASSETS}/m/conn1.mp4`,
    `${ASSETS}/m/conn2.mp4`,
    `${ASSETS}/m/conn3.mp4`,
    `${ASSETS}/m/conn4.mp4`,
    `${ASSETS}/m/conn5.mp4`,
  ],
};

export default function FlightA({ landing = false }: { landing?: boolean } = {}) {
  const worldRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = worldRef.current;
    if (!el || el.dataset.swMounted) return;
    el.dataset.swMounted = 'true';
    mountScrollWorld(el, CONFIG);
    // Official wordmark + pronunciation cue (see lykbl-brand-logo-rule): on this
    // light page the white PNG is inverted to black, same as BrandLogo's
    // dark-variant treatment.
    const brand = el.querySelector('.sw-brand');
    if (brand) {
      brand.innerHTML =
        '<span class="sw-brand__wm"><img src="/lykbl_logo.png" alt="lykbl" class="sw-brand__logo" /><sup>™</sup></span>' +
        '<span class="sw-brand__cue">say it: “likeable”</span>';
    }
    installVariantToggle(el, 'a');
    restoreFlightPosition();
    installFlightTracking(el, 'a');
    // Engine has no unmount API; every exit link it renders is a plain <a>, so
    // leaving this route is always a full document navigation (see FlightB).
  }, []);

  return (
    <>
      <Helmet>
        <title>{landing ? 'lykbl — AI agent teams that grow your audience and shape your offers' : 'lykbl — the quiet floors, waiting for your yes'}</title>
        <meta name="description" content="Scroll up through the studio: a floor-by-floor AI agent team that grows your audience and queues everything for your approval." />
        <meta name="robots" content={landing ? 'index, follow' : 'noindex, nofollow'} />
        <link rel="canonical" href="https://lykbl.com/" />
        <style>{`
          /* Unlayered .sw-root needed — the engine's layered .sw-root sets these
             custom properties directly on the element (see FlightB). Dawn/light
             theme matching the Warm Paper system. */
          :root, .sw-root {
            --sw-bg: #f5f3ee;
            --sw-ink: #171e1c;
            --sw-ink-soft: #6b6f68;
            --sw-accent: #0e7c6e;
            --sw-font-display: 'Space Grotesk', system-ui, sans-serif;
            --sw-font-body: 'DM Sans', system-ui, sans-serif;
          }
          html, body { background: #f5f3ee; }
          #root > div.fixed.bottom-6.right-6 { display: none; }
          /* Site-style CTAs: ink pill with lime text (matches the classic nav). */
          .sw-topcta, .sw-btn--primary { background: #171e1c; color: #cdf355; }
          .sw-btn--ghost { color: #171e1c; border-color: rgba(23, 30, 28, 0.3); }
          /* Brand lockup: white wordmark inverted to black for light background. */
          .sw-brand__wm { display: inline-flex; align-items: flex-start; }
          .sw-brand__logo { height: 28px; width: auto; object-fit: contain; filter: invert(1) hue-rotate(180deg); }
          .sw-brand__wm sup { font-size: 8px; color: rgba(23, 30, 28, 0.5); margin-top: 2px; margin-left: 1px; }
          .sw-brand__cue { padding-left: 12px; border-left: 1px solid rgba(23, 30, 28, 0.22); font-size: 0.8rem; color: rgba(23, 30, 28, 0.55); white-space: nowrap; }
          @media (max-width: 720px) { .sw-brand__cue { display: none; } }
          /* Dawn/Night variant switcher (light theme). */
          .sw-topright { display: flex; align-items: center; gap: 12px; }
          .sw-vt { display: flex; gap: 2px; padding: 3px; border-radius: 999px; background: rgba(23, 30, 28, 0.08); }
          .sw-vt a { text-decoration: none; font-size: 0.82rem; font-weight: 600; padding: 7px 14px; border-radius: 999px; color: rgba(23, 30, 28, 0.6); transition: color 0.2s; }
          .sw-vt a:hover { color: #171e1c; }
          .sw-vt a.is-active { background: #171e1c; color: #f5f3ee; }
        `}</style>
      </Helmet>
      <h1 className="sr-only">Grow your audience. Turn attention into offers. AI agent teams working floor by floor, queueing everything for your approval.</h1>
      <div ref={worldRef} />
    </>
  );
}
